import { useFocusOnMount } from "../../../../../shared/hooks/useFocusOnMount";
import { SUPPORT_HREF } from "../../lib/support";
import type { BookingOutcome as Outcome } from "../../features/booking/operations";
import { receiptFrom } from "../../features/receipt/receipt";
import { useTranslation } from "../../i18n/useTranslation";
import { SERVER_COPY_KEYS_PINNED, reviewCopyFor } from "../../i18n/serverCopy";
import Footer from "../../components/Footer";
import { FlowFrame } from "../../components/PageShell";
import { useOutcomeTitle } from "./outcomeTitle";


interface Props {
  outcome: Outcome;
  /** CONFIRMED or REQUESTED, already proved consistent by `isAuthorizedPilotOutcome`. */
  kind: "confirmed" | "requested";
  clientEmail: string;
  /**
   * GUEST CHECKOUT ONLY (booking-v1 1.11). Absent on the signed-in account
   * path, which keeps its behaviour exactly.
   *
   * `url` is the management link AFTER `acceptManagementUrl`: a validated
   * `/a/<token>` on this host, or null. With a URL the screen offers the one
   * action that uses it. With null it offers help, and says nothing about an
   * email: whether one was sent is not this screen's to know. Product treats a
   * null URL on the normal guest path as a go-live defect.
   */
  guestManage?: { url: string | null };
}

/**
 * The honest post-create surface for the controlled pilot.
 *
 * Ported from the September pilot branch, whose reasoning was right:
 * `Confirmed.tsx` promises a management action and an account handoff that
 * Booking 1.1 does not return, and it cannot represent a manual REQUESTED
 * result at all. This screen renders only what the create response authorizes.
 *
 * THE STATUS PILL IS THE SERVER'S WORDS. Changed 2026-09-02, on the branch
 * consuming `booking-v1@1.2.0`. It used to be derived from `appointment.status`
 * through our own catalogue, and the contract forbids exactly that:
 * `statusLabel` is "Server-owned appointment outcome label. Mobile must not
 * derive it from status." 1.1.0 had already frozen distinct copy for a manual
 * request versus an auto-confirmed booking — the field is 1.1.0's, not part of
 * the 1.2.0 delta — so a client deriving its own label is a second author for
 * the same fact, and the first time the two disagree, the pill and the headline
 * on this screen contradict each other about whether the client has an
 * appointment.
 *
 * `kind` still picks the pill's TONE and the requested notice. That is not
 * deriving the copy: it is a colour and a layout decision keyed to a pairing
 * `isAuthorizedPilotOutcome` has already proved, and the server ships no tone.
 *
 * WHAT IT STILL DELIBERATELY WILL NOT DO: construct a management link. On the
 * signed-in account path it offers none. On the guest path (booking-v1 1.11 draft) it
 * offers exactly the URL Backend returned and `acceptManagementUrl` passed —
 * never one this screen assembled.
 */
function BookingOutcome({ outcome, kind, clientEmail, guestManage }: Props) {
  const { t, fmt, locale, spanishReviewCopy } = useTranslation();
  const { appointment, paymentPlanSummary } = outcome;
  /*
   * THE SERVER'S WORDS, OR THE CATALOGUE'S FOR THE SERVER'S KEY (booking-v1
   * 1.19, option C; i18n/serverCopy.ts). Off until the 1.19 pin: the
   * server's fields, unchanged, on every locale. On, the server still picks
   * WHICH message (`copyKey`) and an unknown key or a missing parameter
   * shows its own text. No parameters are passed yet: their typed fields
   * arrive with the pin, and until then a keyed message that needs one
   * falls back to the server's text rather than a value guessed here.
   */
  const copy = SERVER_COPY_KEYS_PINNED ? reviewCopyFor(outcome.reviewCopy, locale, {}, spanishReviewCopy) : outcome.reviewCopy;
  const requested = kind === "requested";
  /*
   * THE RECEIPT IS THE APPOINTMENT'S, NOT THE PROFILE'S. booking-v1@1.7.0 commits
   * the business name, location and confirmation code at creation. This screen
   * used to take the business name from the profile it had fetched before the
   * booking; that is a different fact, and the profile is no longer a prop, so
   * it cannot quietly come back.
   */
  const receipt = receiptFrom(appointment);
  // The frozen payment terms are part of what this screen states. `isAuthorized-
  // PilotOutcome` has refused any timing other than DUE_AT_APPOINTMENT; null is
  // an unavailable fact and makes the receipt incomplete, visibly.
  const incomplete = receipt.incomplete || appointment.paymentTiming === null;
  const unavailable = t("flow.outcome.unavailableValue");
  /*
   * Focus arrives with the answer. The commit control is gone when this
   * mounts, so a keyboard or screen-reader user must not be left on <body>.
   */
  const headingRef = useFocusOnMount();
  // "Booking details · <business> · Lura" (outcomeTitle.tsx): the committed
  // receipt's business name, left out when that fact is unavailable. Never
  // the server's headline, which may say booked or requested.
  useOutcomeTitle(receipt.businessName.available ? receipt.businessName.value : null);

  return (
    /*
      ONE SHELL FROM THE STEPS TO HERE (c6-2): the outcome is the flow shell's
      outcome variant (components/PageShell.tsx), so the wordmark and
      Appearance are the same nodes the steps had. Alone (a unit test, the
      gallery) the frame draws the same page itself.

      NO APPOINTMENT ID, NO REVISION. This screen once published both as
      `data-appointment-id` and `data-appointment-revision` for recovery
      tests. Product decision 3A removed them: an internal identifier in
      anonymous markup travels with anything that reads the DOM — an
      extension, a support screenshot, a pasted page source — and the
      earlier note that they "leak nothing a viewer does not already have"
      was wrong, because the viewer never had them. Recovery evidence now
      comes from the response the browser received
      (tests/e2e/support/booking-answer.js).
    */
    <FlowFrame
      variant="outcome"
      testId="booking-outcome"
      data={{ "booking-outcome": requested ? "requested" : "confirmed" }}
      lead={null}
    >
        <div className="b2-outcome-content">
          <div className="b-stack g6">
            {/* VERBATIM, and with no local fallback. `isAuthorizedPilotOutcome`
                has already refused an outcome whose label is blank, so there is
                nothing here to substitute for and nothing to substitute. */}
            {/* `t-info`, NOT `t-success`. The kit defines `.b-pill.t-hold` and
                `.b-pill.t-info` and nothing else — the comp has no success pill
                — so `t-success` rendered the CONFIRMED booking with the base
                grey tone while the requested one got its colour, which is the
                wrong one emphasised. Both class names exist, just never
                together; that pairing is now a lint failure. Adding a success
                tone would be inventing design. */}
            <span
              className={`b-pill ${requested ? "t-hold" : "t-info"}`}
              data-testid="booking-outcome-status"
            >
              {copy.statusLabel}
            </span>
            {/* The server's own words for the headline and body. It owns whether
                this reads as a booking or a request; we own never contradicting
                it with a pill or a heading that says otherwise. */}
            <h1
              className="b-display"
              id="booking-outcome-heading"
              ref={headingRef}
              tabIndex={-1}
            >
              {copy.headline}
            </h1>
            {!requested ? (
              <span className="b-seal b2-outcome-seal" aria-hidden="true">✓</span>
            ) : null}
            <p className="b-p">{copy.body}</p>
          </div>

          {/*
            RECEIPT FACTS UNAVAILABLE. Product-closed copy, 2026-09-13.

            The outcome above stays exactly as the server stated it: the booking
            exists. This says only that some details cannot be shown, and offers
            Support. There is deliberately NO reload and no retry — the only
            thing this screen could re-run is createBooking, and inviting another
            attempt over a booking that already exists is how a client ends up
            with two.

            role="status", not "alert": nothing failed, and focus is not moved.
          */}
          {incomplete ? (
            <div
              className="b-notice t-warning"
              role="status"
              data-testid="booking-outcome-receipt-unavailable"
            >
              <div className="b-notice-b">
                <b>{t("flow.outcome.receiptUnavailable.title")}</b>
                {t("flow.outcome.receiptUnavailable.body") ? <p className="b-p">{t("flow.outcome.receiptUnavailable.body")}</p> : null}
                <a className="b-btn v-secondary" href={SUPPORT_HREF} data-testid="booking-outcome-support">
                  {t("flow.outcome.receiptUnavailable.support")}
                </a>
              </div>
            </div>
          ) : null}

          {/*
            THE SUPPLEMENTAL REQUESTED NOTICE WAS REMOVED, not reworded.

            It was the last place on this screen where we spoke over the server
            about what a manual request means — promising the client would hear
            from the professional, and saying "nothing is booked" about a
            request they had just submitted. Product deleted it outright.

            `statusLabel`, `headline` and `body` above are the server's, and
            `isAuthorizedPilotOutcome` has already refused an outcome whose copy
            is missing or contradictory, so there is nothing here to fall back
            to and nothing that needs a fallback.
          */}
          <div className="b2-outcome-facts" data-testid="booking-outcome-summary">
            <>
              <p className="b3-outcome-date">{requested ? t("guest.outcome.requestedWhen", { when: fmt.date(appointment.startTime, appointment.timezone) }) : fmt.date(appointment.startTime, appointment.timezone)}</p>
              <p className="b3-outcome-time">{fmt.time(appointment.startTime, appointment.timezone)} – {fmt.time(appointment.endTime, appointment.timezone)}<br />
                <span className="b-meta" data-testid="booking-outcome-zone">{fmt.zone(appointment.startTime, appointment.timezone)}</span></p>
              <p className="b3-outcome-fact"><strong>{appointment.serviceName}</strong><br /><span className="b-meta">{fmt.duration((Date.parse(appointment.endTime) - Date.parse(appointment.startTime)) / 60_000)}</span></p>
              <span className="b-visually-hidden">{t("guest.outcome.with")}</span>
              <p className="b3-outcome-fact" data-testid="booking-outcome-with">{receipt.businessName.available ? receipt.businessName.value : unavailable}</p>
              <span className="b-visually-hidden">{t("flow.outcome.where")}</span>
              <p className="b3-outcome-fact" data-testid="booking-outcome-where">{receipt.location.available ? receipt.location.value : unavailable}</p>
              <p className="b3-outcome-fact">{t("flow.outcome.payAtAppointment")} · <strong>{fmt.money(paymentPlanSummary.remainingInPersonInCents)}</strong></p>
              {requested ? <p className="b-meta"><span>{t("appt.status.label")}</span> · <span data-testid="booking-outcome-status-row">{copy.statusLabel}</span></p> : null}
              <div className="b3-outcome-reference"><span>{requested ? t("flow.outcome.referenceNumber") : t(guestManage ? "flow.outcome.confirmation" : "flow.outcome.confirmationNumber")}</span>
                <b data-testid="booking-outcome-confirmation">{receipt.confirmationCode.available ? receipt.confirmationCode.value : unavailable}</b></div>
              <p className="b-meta">{t("flow.outcome.email")} · {clientEmail}</p>
            </>
          </div>

          {/* NO CANCELLATION POLICY HERE. Product ruled on 2026-09-02 that the
              post-booking outcome omits policy copy entirely until Backend
              returns appointment-scoped display copy in `CreateBookingResult`.
              The block that stood here was OUR words keyed off the policy
              ENUM — the last client-authored financial wording on this
              surface.

              Reusing the profile's `cancellationPolicyDisplay` would not fix
              it: that states the policy as of profile FETCH for an appointment
              created under whatever applied at COMMIT, and the contract has a
              `CONFLICT` failure for exactly that drift.

              The server-owned PRE-booking policy on StepReview is unaffected
              and stays — it is sourced from the contract, and it is the screen
              where a client decides. */}

          {/* The appointment happens where the professional is, so a client in
              another timezone reading their own local times would arrive wrong. */}
          {/* Needs the committed business name; when that fact is unavailable
              the notice above already says so, and no other name stands in. */}
          {receipt.businessName.available ? (
            <p className="b-meta">{t("flow.outcome.zoneNote", { business: receipt.businessName.value })}</p>
          ) : null}
          {t("flow.outcome.nothingCharged") ? <p className="b-meta">{t("flow.outcome.nothingCharged")}</p> : null}
          <Footer />
        </div>
        {guestManage ? (
          <div className="b2-outcome-foot">
            {guestManage.url ? (
              /* A BEARER CREDENTIAL in one href and nowhere else: not in a
                 data attribute, a title, a log line or an analytics event.
                 Same tab, same origin; `/a/**` is served with no-referrer. */
              <a className="b-btn v-primary is-full" href={guestManage.url} rel="noreferrer" data-testid="booking-outcome-manage">
                {requested ? t("guest.manage.requested") : t("guest.manage.confirmed")}
              </a>
            ) : (
              <a className="b-btn v-secondary is-full" href={SUPPORT_HREF} data-testid="booking-outcome-help">
                {t("guest.help")}
              </a>
            )}
          </div>
        ) : null}
    </FlowFrame>
  );
}

export default BookingOutcome;
